import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ImageField, InputField, SelectField } from "@/components/ui/form";
import PhoneNumberField from "@/components/ui/form/phone-number-field";
import ToggleSwitch from "@/components/ui/form/toggle-switch";
import {
  createFormData,
  useSimpleForm,
  validateObject,
} from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Country, State } from "country-state-city";
import type { Business } from "@/store/Business";
import SetupLayout from "@/components/ui/layout/setup-layout";
import { CreateShopFormSchema } from "@/components/shop/shop-meta";
import { useNavigate } from "@tanstack/react-router";
import { useTranslate } from "@/hooks/use-translate";

export const Route = createFileRoute("/(onboarding)/setup/create-shop")({
  component: CreateShopSetupPage,
});

const TOTAL_STEPS = 3;

function CreateShopSetupPage() {
  const store = useAppStore();
  const business = store.auth.session?.user.business as Business;
  const navigate = useNavigate();
  const { t } = useTranslate();

  const [step, setStep] = useState(1);

  const form = useSimpleForm({
    name: "",
    phone_number: "",
    email: "",
    use_business_address: false,
    country: "",
    region: "",
    city: "",
    address: "",
    logo: { file: undefined, src: "" },
  });

  // -----------------------------------------------------------------
  // Initialise country & region (Cameroon default)
  // -----------------------------------------------------------------
  useEffect(() => {
    store.auth.fetchSession();
    form.setValue("country", "CM");
    const states = State.getStatesOfCountry("CM");
    if (states.length > 0) {
      form.setValue("region", states[0].isoCode);
    }
  }, []);

  // -----------------------------------------------------------------
  // Navigation
  // -----------------------------------------------------------------
  const nextStep = () => {
    store.errors.reset();
    const schema = CreateShopFormSchema[step - 1];
    if (schema) {
      const validation = validateObject(form.values, schema);
      if (!validation.valid) {
        store.errors.setMany(validation.errors);
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // -----------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------
  const submit = async () => {
    store.errors.reset();
    store.shop
      .create(
        createFormData({
          ...form.values,
          business_id: business.id,
        })
      )
      .then(() => {
        navigate({ to: "/" });
      })
      .catch(store.errors.catch);
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <SetupLayout
      image="/assets/setup-4.svg"
      title={t("Add a shop")}
      description={t(
        "Add a new shop to your business. Fill in the details step-by-step."
      )}
    >
      <div className="md:h-screen">
        <div className="flex items-center justify-center w-full h-full p-4 md:p-0">
          <div className="w-full md:w-[500px]">
            {/* ---- Progress bar ---- */}
            <div className="flex items-center gap-2 mb-8">
              {[1, 2, 3].map((i, index: number) => (
                <div
                  key={`stepindix${index}`}
                  className={cn("rounded-md h-2 bg-gray-200 w-1/3", {
                    "bg-primary-400": step === i,
                  })}
                />
              ))}
            </div>
            <div>{store.errors.render()}</div>

            {/* ---- Step 1: Shop Info ---- */}
            {step === 1 && (
              <>
                <h3 className="font-semibold text-xl mt-4">
                  {t("Shop information")}
                </h3>
                <div className="grid grid-cols-1 gap-4 mt-6">
                  <InputField
                    label={t("Shop Name")}
                    name="name"
                    value={form.values.name}
                    onChange={form.handleChange}
                    error={store.errors.values.name}
                    placeholder="e.g., SalesBox Downtown"
                  />

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-900">
                      {t("Phone number")}
                    </label>
                    <PhoneNumberField
                      value={form.values.phone_number}
                      onValueChange={(v) => form.setValue("phone_number", v)}
                      error={store.errors.values.phone_number}
                    />
                  </div>

                  <InputField
                    label={t("Email address")}
                    name="email"
                    type="email"
                    value={form.values.email}
                    onChange={form.handleChange}
                    error={store.errors.values.email}
                  />
                </div>
              </>
            )}

            {/* ---- Step 2: Address ---- */}
            {step === 2 && (
              <>
                <h3 className="font-semibold text-xl mt-4">
                  {t("Shop address")}
                </h3>

                <div className="mt-6">
                  <ToggleSwitch
                    label={t("Use business address for shop location")}
                    checked={form.values.use_business_address}
                    onChange={(checked: boolean) => {
                      form.setValue("use_business_address", checked);
                      if (checked && business?.address) {
                        form.setValues({
                          country: business.location.country,
                          region: business.location.region,
                          city: business.location.city,
                          address: business.location.address,
                        });
                      }
                    }}
                  />
                </div>

                <div className="space-y-4 mt-6">
                  <SelectField
                    label={t("Country")}
                    name="country"
                    value={form.values.country}
                    onChange={form.handleChange}
                    options={Country.getAllCountries().map((c) => ({
                      label: c.name,
                      value: c.isoCode,
                    }))}
                    required
                  />

                  <SelectField
                    label={t("State/Region")}
                    name="region"
                    value={form.values.region}
                    onChange={form.handleChange}
                    options={
                      form.values.country
                        ? State.getStatesOfCountry(form.values.country).map(
                            (s) => ({
                              label: s.name,
                              value: s.isoCode,
                            })
                          )
                        : []
                    }
                    required
                  />

                  <InputField
                    label={t("City")}
                    name="city"
                    value={form.values.city}
                    onChange={form.handleChange}
                    error={store.errors.values.city}
                  />

                  <InputField
                    label={t("Street address")}
                    name="address"
                    value={form.values.address}
                    onChange={form.handleChange}
                    error={store.errors.values.address}
                  />
                </div>
              </>
            )}

            {/* ---- Step 3: Logo ---- */}
            {step === 3 && (
              <>
                <h3 className="font-semibold text-xl mt-4">
                  {t("Shop logo (optional)")}
                </h3>
                <div className="mt-6">
                  <ImageField
                    name="logo"
                    image={form.getImageValue("logo")}
                    onImageChange={form.handleImageChange("logo")}
                    error={store.errors.values.logo}
                    id="logo"
                    className="h-64 w-64 border-dashed"
                  />
                </div>
              </>
            )}

            {/* ---- Navigation Buttons ---- */}
            <div className="space-y-4 mt-8">
              {step < TOTAL_STEPS ? (
                <Button onClick={nextStep} className="f w-full">
                  {t("Continue")}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  loading={store.shop.loading("create")}
                  className="w-full py-2"
                >
                  {t("Submit the shop")}
                </Button>
              )}

              {step > 1 && (
                <Button
                  color="outline"
                  onClick={prevStep}
                  className="w-full border-none hover:bg-primary-100 transition-all duration-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("Back")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SetupLayout>
  );
}
